import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';

import { isOrderTypeTakeoverEnabled } from './is-order-type-takeover-enabled';

/**
 * Minimal shape needed to evaluate the combined gate. `id` drives the
 * per-dispensary flag eval; `storeSettings` drives the admin setting.
 */
type DispensaryWithTakeoverGate = {
  id?: string | null;
  storeSettings?: {
    enableOrderTypeTakeoverModal?: boolean | null;
  } | null;
} | null;

/**
 * Feature-rollout flag for the order-type takeover. This is the SAME flag that
 * gates the admin "Enable Order Type Modal" setting's visibility
 * (apps/admin .../store-menu-options.tsx), reused here as a belt-and-suspenders
 * guard on the consumer side: the takeover only activates when BOTH this flag is
 * on and the admin store setting (`enableOrderTypeTakeoverModal`) is enabled.
 *
 * Read via `useDispensaryFlag`, the standard consumer helper for per-dispensary
 * flags. It evaluates the flag's (context-independent) value against the
 * dispensary id client-side, so — unlike a raw context-targeted `useFlags` read
 * — it is NOT subject to the SSR-bootstrap context race (ECOMM-11202). The flag
 * may be a plain boolean (global on/off) or a `{ enabledForDispensaries: [...] }`
 * JSON flag for a per-store rollout.
 */
export const ORDER_TYPE_TAKEOVER_FLAG = 'growth.ecomm.order-type-takeover-setting.rollout';

/**
 * Single source of truth for whether the order-type takeover is active for a
 * dispensary. Combines the admin store setting with the rollout flag. Defaults
 * to `false` when either is unset. Must be called from a React render.
 */
export function useIsOrderTypeTakeoverEnabled(dispensary: DispensaryWithTakeoverGate | undefined): boolean {
  const isFlagEnabled = useDispensaryFlag(ORDER_TYPE_TAKEOVER_FLAG, dispensary?.id ?? undefined, false);

  return Boolean(isFlagEnabled) && isOrderTypeTakeoverEnabled(dispensary);
}
